import React, { createContext, useContext, useMemo } from 'react';
import { usePlayer } from '$hooks/services';


/* ========================================================================= */
/* Hook(s) */
/* ========================================================================= */

const Context = createContext();

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @component Player
 * @props (see propTypes)
 * @example:
 *     <Player {...}>
 *         <Embed />
 *         <Placeholder>
 *             <PlayButton>...</PlayButton>
 *         </Placeholder>
 *     </Player>
 *
 * 1. Only hydrates when the player, element reference on playing state changes.
 */
const Player = ({
    shouldToggleVisibility,
    isAutoPlayed,
    shouldPrerender,
    ...remainingProps
}) => {
    const player = usePlayer({ isAutoPlayed });

    const context = useMemo(() => ({
        ...player,
        shouldToggleVisibility,
        shouldPrerender,
        isAutoPlayed
    }), [
        player.elementRef,
        player.player,
        player.isPlaying,
    ]);

    return (
        <Context.Provider value={context}>
            <div {...remainingProps} />
        </Context.Provider>
    );
};

/**
 * @component Embed
 * @props (see propTypes)
 *
 * Represents the play button container within the video player.
 */
const Embed = props => {
    const { ref, isPlaying, shouldToggleVisibility, shouldPrerender } = useContext(Context);

    if (!isPlaying && !shouldPrerender) {
        return null;
    }

    return (
        <div
            autoFocus={true}
            hidden={shouldToggleVisibility && !isPlaying}
            ref={ref}
            {...props} />
    );
};

/**
 * @component Placeholder
 * @props (see propTypes)
 *
 * Represents the play button container within the video player.
 */
const Placeholder = props => {
    const { isPlaying, play, shouldToggleVisibility } = useContext(Context);

    return (
        <div
            hidden={shouldToggleVisibility && isPlaying}
            onClick={play}
            style={{ cursor: 'pointer' }}
            {...props} />
    );
};

/**
 * @component PlayButton
 * @props (see propTypes)
 *
 * Represents the play button container within the video player.
 */
const PlayButton = props => {
    const { play } = useContext(Context);

    return (
        <button
            onClick={play}
            type='button'
            {...props} />
    );
};

/**
 * @component PauseButton
 * @props (see propTypes)
 *
 * Represents the pause button container within the video player.
 */
const PauseButton = props => {
    const { pause } = useContext(Context);

    return (
        <button
            onClick={pause}
            type='button'
            {...props} />
    );
};

/* ========================================================================= */
/* Typing & export(s) */
/* ========================================================================= */

Player.propTypes = {};

Embed.propTypes = {};

Placeholder.propTypes = {};

PlayButton.propTypes = {};

PauseButton.propTypes = {};

const PlayerEmbed = Embed;
const PlayerPlaceholder = Placeholder;
const PlayerPlayButton = PlayButton;
const PlayerPauseButton = PauseButton;
const PlayerContextConsumer = Context.Consumer;

export {
    Player,
    PlayerEmbed,
    PlayerPlaceholder,
    PlayerPlayButton,
    PlayerPauseButton,
    PlayerContextConsumer,
};
